import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useAuth } from '../utils/AuthContext';
import Layout from './Layout';
import UploadCompareService from './UploadCompareService';
import ConfirmTablesForScoreProcessing from './ConfirmTablesForScoreProcessing';
import { useLocation } from 'react-router-dom';
import { Environment } from '../utils/Environment';
import ProcessDiamScore from './ProcessDiameterScoring';
import ProcessCofScores from './ProcessDistanceScores';
import ProcessWetlandScore from './ProcessWetlandScores';
import ProcessRailScore from './ProcessRailScores';
// import ProcessConcreteOrSteel from './ProcessConcreteAndSteel';
import ProcessRoadScore from './ProcessRoadScores';
import ConfirmCostColumns from './ConfirmCostCalcValues';
// import GetHydroJoinColumn from './GetHydroJoinColumn';
import LoadCostScoringData from './LoadDataForCostScoring';
import PerformCostCalculation from './PerformCofCostCalculation';
import { postWithUsername } from "../services/fetchWrapper";
import UploadService from './UploadService';

const apiUrl = Environment.getApiUrl();

const steps = [
  () => <UploadService />,
  () => <UploadCompareService />,
  () => <ConfirmTablesForScoreProcessing />,
  () => <ProcessDiamScore />,
  () => <ProcessCofScores />,
  () => <ProcessWetlandScore />,
  () => <ProcessRailScore />,
  // () => <ProcessConcreteOrSteel />,
  () => <ProcessRoadScore />,
  // () => <div >flag bridge assets?</div>,
  () => <ConfirmCostColumns />,
  // () => <GetHydroJoinColumn />,
  () => <LoadCostScoringData />,
  () => <PerformCostCalculation />
];

const StepFlow: React.FC = () => {
  const username = localStorage.getItem("userName") || "";
  const [step, setStep] = useState(0);
  const { isLoggedIn, userName, logout } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dummy, setDummy] = useState(0); // Add this state for forcing re-render


  const stepCompletionKeys = [
    null,
    null,
    "tablesSaved",
    "diamScoreDone",
    "cofScoresDone",
    null,
    null,
    null,
    // "wetlandScoreDone",
    // "railScoreDone",
    // "roadScoreDone",
    "costColumnsConfirmed",
    "costScoringDataLoaded",
    "costCalculationDone"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      const key = stepCompletionKeys[step];
      if (key && localStorage.getItem(key) === "true") {
        setDummy(d => d + 1); // force re-render
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [step]);

  // uncomment to disable
  const isNextDisabled = () => {
    return false;
  };

  // const isNextDisabled = () => {
  //   const key = stepCompletionKeys[step];
  //   if (!key) return false; // No key required, always enabled
  //   return localStorage.getItem(key) !== "true";
  // };

  useEffect(() => {
    const fetchLatestJobId = async () => {
      try {
        const client_id = localStorage.getItem("client_id");
        if (!client_id) {
          setError("No client ID found. Please log in again.");
          setLoading(false);
          return;
        }
        let response = await fetch(`${apiUrl}/get-latest-job-id?client_id=${encodeURIComponent(client_id)}`);
        let data = null;
        if (response.ok) {
          data = await response.json();
        } else if (response.status === 500) {
          // Treat 500 as "no job found", skip to fallback
          data = {};
        } else {
          throw new Error("Failed to fetch user job id");
        }
        if (data.job_id) {
          localStorage.setItem("job_id", data.job_id);
        } else {
          // Fallback: get job_id from user table (first-time user)
          response = await postWithUsername(`${apiUrl}/get-client-data`, username);
          if (!response.ok) throw new Error("Failed to fetch user job id");
          data = await response.json();
          if (data.job_id) {
            localStorage.setItem("job_id", data.job_id);
          } else {
            setError("Unexpected error: No latest job ID returned from server.");
          }
        }
      } catch (err) {
        setError("Unexpected error: Could not retrieve latest job ID.");
      } finally {
        setLoading(false);
      }
    };
    fetchLatestJobId();
  }, [location.pathname]);

  const handleNext = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  if (loading) {
    return (
      <Layout isLoggedIn={isLoggedIn} onLogout={logout} userName={userName}>
        <Box sx={{ width: '100%', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="h6">Loading latest job ID...</Typography>
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout isLoggedIn={isLoggedIn} onLogout={logout} userName={userName}>
        <Box sx={{ width: '100%', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'error.main' }}>
          <Typography variant="h6">{error}</Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout isLoggedIn={isLoggedIn} onLogout={logout} userName={userName}>
      <Box
        // white space settings below menu
        sx={{
          width: '100vw',
          maxWidth: 1400,
          margin: 'auto',
          mt: 1,
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Step circle display */}
        <Box sx={{ mb: 2, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
          {steps.map((_, index) => {
            const isActive = index === step;
            const isCompleted = index < step;
            const isConnectorActive = index < step;

            return (
              <React.Fragment key={index}>
                <Box
                  role="img"
                  aria-label={`Step ${index + 1}${isActive ? ' current' : ''}${isCompleted ? ' completed' : ''}`}
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    boxSizing: 'border-box',
                    border: '3px solid #5FA4ED',
                    backgroundColor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      backgroundColor: isActive || isCompleted ? '#5FA4ED' : 'transparent',
                    }}
                  />
                </Box>
                {index < steps.length - 1 && (
                  <Box
                    aria-hidden
                    sx={{
                      width: { xs: 12, sm: 20 },
                      height: 3,
                      mx: 0.5,
                      borderRadius: 999,
                      backgroundColor: isConnectorActive ? '#5FA4ED' : '#D7E7FA',
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
          <span style={{ display: "none" }}>{dummy}</span>
        </Box>
        <Box
          sx={{
            mb: 4,
            width: '100%',
            textAlign: 'center',
            maxHeight: 800, // adjust as needed
            minHeight: 400,
            overflowY: 'auto',
            overflowX: 'auto',
          }}
        >
          {steps[step]()}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center' }}>
          {step < steps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isNextDisabled()}
              sx={{ backgroundColor: "#0373B5", minWidth: "150px" }}
            >
              Continue
            </Button>
          )}
          {step > 0 && (
            <Button
              variant="text"
              onClick={handleBack}
              disabled={step === 0}
              sx={{
                fontSize: "0.82rem", // slightly smaller
                color: "#000000", // black text
                textDecoration: "underline",
                textTransform: "none",
                borderColor: "#000000",
                minWidth: "auto",
                px: 1.5,
                "&:hover": {
                  borderColor: "#000000",
                  backgroundColor: "transparent",
                  textDecoration: "underline",
                },
              }}
            >
              Go Back
            </Button>
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default StepFlow;